export type SimpleStepKey = 'file' | 'lor' | 'ack';

export type ClaimsFilingProgress = {
  file: boolean;
  lor: boolean;
  ack: boolean;
  clientReviewed: boolean;
  callFailed: boolean;
  /** ISO date strings for when a step was marked complete (client-side). */
  fileAt?: string | null;
  lorAt?: string | null;
  ackAt?: string | null;
};

export interface ClaimsFilingProgressStore {
  get(coverageId: string): ClaimsFilingProgress;
  set(
    coverageId: string,
    partial: Partial<ClaimsFilingProgress>,
  ): ClaimsFilingProgress;
}

const STORAGE_KEY = 'claimsFilingProgress:v1';

const read = (): Record<string, Partial<ClaimsFilingProgress>> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw
      ? (JSON.parse(raw) as Record<string, Partial<ClaimsFilingProgress>>)
      : {};
  } catch {
    return {};
  }
};

const write = (store: Record<string, Partial<ClaimsFilingProgress>>) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore in demo */
  }
};

const normalize = (
  p?: Partial<ClaimsFilingProgress>,
): ClaimsFilingProgress => ({
  file: !!p?.file,
  lor: !!p?.lor,
  ack: !!p?.ack,
  clientReviewed: !!p?.clientReviewed,
  callFailed: !!p?.callFailed,
  fileAt: p?.fileAt ?? null,
  lorAt: p?.lorAt ?? null,
  ackAt: p?.ackAt ?? null,
});

export const localProgressStore: ClaimsFilingProgressStore = {
  get(coverageId) {
    const store = read();
    return normalize(store[coverageId]);
  },
  set(coverageId, partial) {
    const store = read();
    const prev = store[coverageId] || {};
    const nowIso = new Date().toISOString();
    const shouldSet = (key: 'file' | 'lor' | 'ack') =>
      (partial as any)[key] === true && !(prev as any)[`${key}At`];
    const next = {
      ...prev,
      ...partial,
      fileAt: shouldSet('file') ? nowIso : (prev as any).fileAt,
      lorAt: shouldSet('lor') ? nowIso : (prev as any).lorAt,
      ackAt: shouldSet('ack') ? nowIso : (prev as any).ackAt,
    } as Partial<ClaimsFilingProgress>;
    store[coverageId] = next;
    write(store);
    return normalize(next);
  },
};

let currentStore: ClaimsFilingProgressStore = localProgressStore;

export const setProgressStore = (store: ClaimsFilingProgressStore) => {
  currentStore = store;
};

export const getCoverageProgress = (coverageId: string) =>
  currentStore.get(coverageId);
export const setCoverageProgress = (
  coverageId: string,
  partial: Partial<ClaimsFilingProgress>,
) => currentStore.set(coverageId, partial);
